import React from 'react';
import _ from 'lodash';
import * as SiteManager from '../domain/site-manager';
import { useData, dataFromDataset } from './DataProviderCache';

import { useAuthHelper } from '../../components/AuthContextProvider';
import { isDataLoaded, Data, makeLoadedData, ExtractDataType } from '../../types/data-providers';
import { getPrecisionValue } from '../../lib/precision-helper';

type ContextState = Data<{
    eventCount: number | undefined;
    online: boolean | undefined;
    offlinedevicecount: number | undefined;
    channelData: Array<{
        id: number | string;
        channelName: string; // Temperature, TemperatureRaw, FlowMA, etc.
        deviceId: string;
        deviceName: string;
        value: number | string | undefined;
        units: string;
        iconClass: string;
    }>;
    message: string | undefined;
}>;

const Context = React.createContext<ContextState | null>(null);

function useSiteData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useSiteData must be used within a SiteDataProvider');
    }
    return contextState;
}

const SiteDataProvider: React.FC<{ siteId: string }> = (props) => {
    const authHelper = useAuthHelper();

    const { dataset } = useData({
        cacheKey: 'site-condition-data',
        id: props.siteId,
        dataFn: React.useCallback(
            () => SiteManager.getSiteCondition(authHelper, { siteId: props.siteId }),
            [authHelper, props.siteId]
        ),
    });
    const siteConditionData = dataFromDataset(dataset);

    const value = React.useMemo(() => {
        if (!isDataLoaded(siteConditionData)) {
            return siteConditionData;
        }

        const channelData = siteConditionData.value.channelData
            .filter((channelDatum) => !_.isNil(channelDatum.channelName) && !_.isNil(channelDatum.value))
            .map((channelDatum) => ({
                channelName: channelDatum.channelName || '',
                deviceId: channelDatum.deviceId,
                deviceName: channelDatum.deviceName,
                value: getPrecisionValue(channelDatum.value),
                units: channelDatum.unit || '',
                id: channelDatum.id,
                iconClass: channelDatum.iconClass,
            }));

        return makeLoadedData<ExtractDataType<ContextState>>({
            ...siteConditionData.value,
            channelData,
            message: undefined,
            
        });
    }, [siteConditionData]);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const TestSiteDataProvider: React.FC<{ value: ContextState }> = (props) => (
    <Context.Provider value={props.value}>{props.children}</Context.Provider>
);

export { SiteDataProvider, TestSiteDataProvider, useSiteData };
